import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, ValidationErrors } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { DEFAULT_CORRELATION } from 'webapp/models-custom/regressor-metric';

export default class ForecastsContextCorrelationComponent extends PopupInnerContextComponent<PopupData.Forecast.Correlation> {
	@service('account') declare accountService: Service.Account;

	@tracked value = DEFAULT_CORRELATION;
	@tracked inputValue = String(DEFAULT_CORRELATION);
	@tracked errors: ValidationErrors<'value'> = {};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const data = this.popupData;
		this.value = data.currentValue!;
		this.inputValue = String(this.value);
	}

	get strength(): string {
		const absValue = Math.abs(this.value);
		if (absValue > 0.7) return 'strong';
		if (absValue > 0.3) return 'moderate';
		return 'weak';
	}

	get displayValue(): string {
		return (this.value >= 0 ? '+' : '') + this.value.toFixed(2);
	}

	validate(): boolean {
		const errors: ValidationErrors<'value'> = {};

		if (this.value < -1 || this.value > 1) {
			errors.value = [{ message: 'Value must be between -1 and 1' }];
		}

		this.errors = errors;
		return Object.keys(errors).length === 0;
	}

	@action
	updateValue(value: number | null): void {
		if (value !== null && value !== undefined) {
			this.value = value;
			this.inputValue = String(value);
		} else {
			this.value = DEFAULT_CORRELATION;
			this.inputValue = String(DEFAULT_CORRELATION);
		}
	}

	@action
	apply(): void {
		if (!this.validate()) {
			return;
		}
		this.popupData.onApply(this.value);
		this.close();
	}

	@action
	recalculateCorrelation(): void {
		this.popupData.onRecalculate();
		this.close();
	}
	<template>
		<div class="popup-correlation">
			<div class="popup-correlation__header">
				<div
					class={{joinClasses
						(array "correlation-value-display" "correlation-value-badge" this.strength)
					}}
				>
					{{this.displayValue}}
				</div>
			</div>

			<div class="popup-correlation__content">
				<p class="correlation-description">
					Correlation factor calculated based on the historical trends between this metric
					and the main forecasted metric.
				</p>

				<div class="popup-correlation__input-section">
					<h4>Manually set correlation factor:</h4>
					<FormInputDecimalComponent
						@value={{this.inputValue}}
						@onChange={{this.updateValue}}
					/>
					<RowErrorComponent @items={{this.errors.value}} />
				</div>

				<div class="popup-correlation__description">
					<p>
						Valid input are numerical values from -1.0 to 1.0. Correlation factor will
						influence the forecast of your main metric.
						{{#unless
							this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
						}}
							<a href="#" class="learn-more-link">Learn more</a>
						{{/unless}}
					</p>
				</div>
			</div>

			<div class="popup-correlation__actions">
				<a role="button" class="cancel" {{on "click" this.recalculateCorrelation}}>
					Reset to default
				</a>
				<SubmitButtonComponent @text="Apply" @extraClass="save" @onTrigger={{this.apply}} />
			</div>
		</div>
	</template>
}
